/**
 * aC Men's Hub の課題管理の取込み（Slack の投稿を声にする処理・チャンネルの設定・本文削除・月1回の点検）の語彙と API 契約。
 *
 * 取込みの状況（チャンネルごとの状態・最後の実行）と点検を API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）で
 * 同じ型から扱う。チャンネルが「止まっています」かどうかの判定もここを唯一の定義にする。
 * 定期処理の種類・起動のきっかけ・結果・失敗の区分は Backlog 同期と共通の `issue-mgmt-backlog.ts` の定義を使う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の Slack 取込みの語彙（失敗の区分・論点なしの理由・チャンネルの状態）と、取込みの状況・今すぐ取り込む・チャンネルの設定・本文削除・点検の API 契約を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
import { IssueJobFailureCodeSchema, IssueJobRunStatusSchema, IssueJobTriggerSchema, } from './issue-mgmt-backlog.js';
import { IssueCreatedResultSchema, VoiceIdSchema } from './issue-mgmt.js';
/**
 * AI が投稿に論点なし（課題が無い）とした理由の区分。お礼・あいさつ・雑談／連絡・お知らせ／日程調整／
 * 質問への回答・作業の完了報告／テスト・誤送信・意味を持たない通知／その他。
 * 理由の文は持たず、この区分だけを除外の札と BigQuery `issue__ai_suggestions` の reason_code に使う。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business AI が投稿を論点なしとして除外した理由の区分を定める
 * @graph-connects none
 */
export const IssueNoPointReasonSchema = z.enum([
    'greeting',
    'announcement',
    'scheduling',
    'answer_or_report',
    'test_or_noise',
    'other',
]);
/**
 * 本文を削除した声・論点に入れる文（論点のタイトルは空にできないので、この文に置き換える）。
 * @graph-connects none
 */
export const ISSUE_DELETED_TEXT = '削除済み';
/**
 * チャンネルの取込みが何回続けて失敗したら「止まっています」にするか（通知もこの回数に達したときに1回送る）。
 * @graph-connects none
 */
export const INGEST_CHANNEL_STOP_AFTER_FAILURES = 2;
/**
 * Slack のチャンネル ID（公開は C、古い非公開は G で始まる）。
 * @graph-connects none
 */
export const SlackChannelIdSchema = z.string().regex(/^[CG][A-Z0-9]{2,}$/);
/**
 * 取込みチャンネルの状態。取込み中／招待待ち（ai-fassy が保存していないまま足した）／止まっています／停止（人が止めた）。
 * @graph-connects none
 */
export const IngestChannelStateSchema = z.enum([
    'ingesting',
    'awaiting_invite',
    'stopped',
    'disabled',
]);
/**
 * チャンネルの取込みの失敗の記録。since は続けて失敗し始めた時刻、consecutive は続けて失敗した回数。
 * @graph-connects none
 */
export const IngestChannelFailureSchema = z.object({
    code: IssueJobFailureCodeSchema,
    since: z.string(),
    consecutive: z.number().int().min(1),
});
/**
 * 失敗の記録が「止まっています」か（続けて `INGEST_CHANNEL_STOP_AFTER_FAILURES` 回以上失敗した）。
 * 画面の状態と、止まったときの通知の両方がこの1つの判定を使う（状態と通知がずれないように）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 取込みチャンネルの失敗の記録から、止まっているかを判定する（状態の表示と通知で共通）
 * @graph-connects INGEST_CHANNEL_STOP_AFTER_FAILURES [references] 止まっていますにする回数
 */
export function isIngestChannelStopped(failure) {
    return failure !== null && failure.consecutive >= INGEST_CHANNEL_STOP_AFTER_FAILURES;
}
/**
 * 設定と失敗の記録から、取込みチャンネルの状態を決める。
 *
 * 人が止めたチャンネルは停止。ai-fassy が保存していないまま足したチャンネルは、「招待した」で参加を確かめるまで招待待ち
 * （取り込みに行かないので失敗にも数えない）。それ以外は `isIngestChannelStopped` で止まっていますか取込み中。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 取込みチャンネルの有効・招待待ち・続けて失敗した回数から、取込み中・招待待ち・止まっています・停止のどれかを決める
 * @graph-connects isIngestChannelStopped [calls] 止まっているか
 */
export function deriveIngestChannelState(input) {
    if (!input.enabled)
        return 'disabled';
    if (input.invitePending)
        return 'awaiting_invite';
    return isIngestChannelStopped(input.failure) ? 'stopped' : 'ingesting';
}
/**
 * 取込みチャンネル1行（設定タブ）。lastIngestedAt は最後に取込みを終えた時刻（まだ無ければ null）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 取込みチャンネルの名前・非公開の印・有効・状態・最後に取り込んだ時刻・失敗の記録を表す
 * @graph-connects none
 */
export const IngestChannelSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    isPrivate: z.boolean(),
    enabled: z.boolean(),
    state: IngestChannelStateSchema,
    lastIngestedAt: z.string().nullable(),
    failure: IngestChannelFailureSchema.nullable(),
});
/**
 * 最後の取込みの実行の状態。aborted は締め切りを過ぎても終わった記録が無い実行（時間切れ・強制終了）。
 * @graph-connects none
 */
export const IngestRunStatusSchema = z.enum([
    'running',
    ...IssueJobRunStatusSchema.options,
    'aborted',
]);
/**
 * 最後の取込みの実行（設定タブの定期処理の行）。
 * @graph-connects none
 */
export const IngestRunSummarySchema = z.object({
    runId: z.string(),
    trigger: IssueJobTriggerSchema,
    triggeredBy: z.string().nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    status: IngestRunStatusSchema,
    failureCode: IssueJobFailureCodeSchema.nullable(),
    processedCount: z.number().int().nullable(),
});
/**
 * 月1回の点検の結果の要約。realVoiceCount は除外から振り分けに戻した数（本当は声だったもの）、
 * leakCount は本文を削除した数（伏せ字漏れ）。終えるまでは null。
 * @graph-connects none
 */
export const IngestInspectionSummarySchema = z.object({
    inspectionId: z.string(),
    targetMonth: z.string(),
    startedAt: z.string(),
    startedBy: z.string(),
    finishedAt: z.string().nullable(),
    realVoiceCount: z.number().int().nullable(),
    leakCount: z.number().int().nullable(),
});
/**
 * Slack の保存（ai-fassy が BigQuery に写す処理）が止まっているとみなす古さ（時間）。
 * @graph-connects none
 */
export const SLACK_ARCHIVE_STALE_HOURS = 24;
/**
 * Slack の保存（ai-fassy）が止まっているか。最新時刻が無いか、`SLACK_ARCHIVE_STALE_HOURS` より古ければ止まっている。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business ai-fassy が Slack の投稿を BigQuery に保存した最新時刻から、保存が止まっているかを判定する
 * @graph-connects SLACK_ARCHIVE_STALE_HOURS [references] 止まっているとみなす古さ
 */
export function isSlackArchiveStale(latestSavedAt, now) {
    if (latestSavedAt === null)
        return true;
    const savedMs = Date.parse(latestSavedAt);
    if (!Number.isFinite(savedMs))
        return true;
    return now.getTime() - savedMs > SLACK_ARCHIVE_STALE_HOURS * 60 * 60 * 1000;
}
/**
 * Slack の保存（ai-fassy）の状況。latestSavedAt は保存の最新時刻（まだ無ければ null）、stale は止まっているか。
 * @graph-connects none
 */
export const SlackArchiveHealthSchema = z.object({
    latestSavedAt: z.string().nullable(),
    stale: z.boolean(),
});
/**
 * 取込みの状況（設定タブのチャンネル・定期処理の取込みの行・前回の点検・Slack の保存の状況）。
 * slackArchive は Slack の保存の最新時刻を読めなかったとき null（止まっているとは出さない）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の取込みチャンネルの一覧と状態、最後の取込みの実行、前回の点検の結果、Slack の保存（ai-fassy）の最新時刻を返す
 * @graph-connects none
 */
export const IngestStatusResponseSchema = z.object({
    channels: z.array(IngestChannelSchema),
    lastRun: IngestRunSummarySchema.nullable(),
    latestInspection: IngestInspectionSummarySchema.nullable(),
    slackArchive: SlackArchiveHealthSchema.nullable(),
});
/**
 * 1回の取込みのチャンネル別の結果。voiceCount は振り分けに出した声、excludedCount は AI が論点なしとして除外した投稿。
 * status の skipped は、持ち時間の中で読み終えられなかった（または読みに行けなかった）チャンネル。失敗には数えず、
 * 次の実行で先に回す。
 * @graph-connects none
 */
export const IngestChannelResultSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    status: z.enum(['success', 'failed', 'skipped']),
    fetchedCount: z.number().int(),
    voiceCount: z.number().int(),
    excludedCount: z.number().int(),
    errorCode: IssueJobFailureCodeSchema.nullable(),
});
/**
 * 今すぐ取り込むを受け付けた結果。取込みは裏で動き、結果は取込みの状況（最後の取込みの実行）で見る。
 * @graph-connects none
 */
export const IngestRunAcceptedSchema = z.object({ runId: z.string() });
/**
 * 1回の取込みの結果。processedCount は振り分けに出した声の数。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の取込み1回の結果（成功・一部失敗・失敗、失敗の区分、チャンネル別の件数と失敗）を表す
 * @graph-connects none
 */
const IngestRunResultSchema = z.object({
    runId: z.string(),
    status: IssueJobRunStatusSchema,
    failureCode: IssueJobFailureCodeSchema.nullable(),
    processedCount: z.number().int(),
    channels: z.array(IngestChannelResultSchema),
});
/**
 * 取込みチャンネルを足す入力。チャンネル名（# は付けても付けなくてもよい）かチャンネル ID。
 * @graph-connects none
 */
export const IngestChannelAddInputSchema = z.object({
    channel: z.string().trim().min(1).max(100),
});
/**
 * 取込みチャンネルを1つ指す入力（「招待した」）。
 * @graph-connects none
 */
export const IngestChannelRefInputSchema = z.object({ channelId: SlackChannelIdSchema });
/**
 * 取込みチャンネルを止める・再開する入力。
 * @graph-connects none
 */
export const IngestChannelEnabledInputSchema = z.object({
    channelId: SlackChannelIdSchema,
    enabled: z.boolean(),
});
/**
 * Slack の投稿 URL を貼って声にする入力。
 * @graph-connects none
 */
export const SlackUrlIngestInputSchema = z.object({
    url: z
        .string()
        .trim()
        .max(500)
        .regex(/\/archives\/[CG][A-Z0-9]+\/p\d{16}/, {
        message: 'Slack の投稿の URL（…/archives/チャンネルID/p…）を貼ってください',
    }),
});
/**
 * Slack の投稿 URL から声にした結果。duplicated はその投稿の声が既にあった（id は既にある声）。
 * @graph-connects none
 */
export const SlackUrlIngestResultSchema = IssueCreatedResultSchema.extend({
    duplicated: z.boolean(),
});
/**
 * 声を1つ指す入力（本文削除・除外から振り分けに戻す）。
 * @graph-connects none
 */
export const VoiceRefInputSchema = z.object({ voiceId: VoiceIdSchema });
/**
 * 点検の行。kind は除外（AI が論点なしとした投稿）か、取り込んだ声か。
 * restored は除外から振り分けに戻した、bodyDeleted は本文を削除した。text は伏せ字済みの本文（削除後は null）。
 * @graph-connects none
 */
export const IngestInspectionRowSchema = z.object({
    kind: z.enum(['excluded', 'voice']),
    voiceId: z.string(),
    channelName: z.string().nullable(),
    isPrivate: z.boolean(),
    postedAt: z.string().nullable(),
    text: z.string().nullable(),
    excludedReason: IssueNoPointReasonSchema.nullable(),
    slackUrl: z.string().nullable(),
    restored: z.boolean(),
    bodyDeleted: z.boolean(),
});
/**
 * 月1回の点検（先月の除外と、先月に取り込んだ声を無作為に抜いたもの）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 月1回の点検の対象（先月の除外と取り込んだ声の無作為な抜き取り）と、終えたときの結果を表す
 * @graph-connects none
 */
export const IngestInspectionSchema = IngestInspectionSummarySchema.extend({
    rows: z.array(IngestInspectionRowSchema),
});
/**
 * 最新の点検の応答（まだ1回も始めていなければ null）。
 * @graph-connects none
 */
export const IngestInspectionResponseSchema = z.object({
    inspection: IngestInspectionSchema.nullable(),
});
/**
 * 点検を終える入力（点検の ID は点検した月 YYYY-MM）。
 * @graph-connects none
 */
export const IngestInspectionFinishInputSchema = z.object({
    inspectionId: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
});
//# sourceMappingURL=issue-ingest.js.map